import {NgClass} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject, Input, OnChanges, OnInit, signal, ViewChild} from '@angular/core';
import {merge, MonoTypeOperatorFunction, NEVER, Observable, of, Subject, switchMap} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, startWith, takeUntil, tap} from 'rxjs/operators';
import {dependOn} from '../../../common/rxjs/depend-on';
import {TypedSimpleChanges} from '../../../common/typed-on-changes';
import {BigintDisplayDecimalPipe} from '../../../components/bigint-display-decimal-pipe/bigint-display-decimal.pipe';
import {CustomScrollbarModule} from '../../../components/custom-scrollbar';
import {destroy$} from "../../../components/destroyable";
import {vToDec} from '../../../components/dex/dex-streaming/utils';
import {DeliverableBalance} from '../../../components/dex/dyn-balances/deliverable-balance';
import {
    Erc20BalanceUpdatesService,
    PartialBalanceUpdate
} from '../../../components/dex/dyn-balances/erc20-balances-updates.service';
import {Erc20BalancesService} from '../../../components/dex/dyn-balances/erc20-balances.service';
import {DisplayDecimalModule} from '../../../components/display-decimal';
import {DynWallet} from '../../../components/dynamic-xyz/dyn-xyz-wc/dyn-remote';
import {DynamicZyxDaemonService} from '../../../components/dynamic-xyz/dynamic-zyx-daemon.service';
import {UPDATES_GATING_PERIOD} from '../../../components/layout/widget/widget-updates-gating';
import {
    PARTIAL_LOADING,
    PartialLoading,
    PartialLoadingIndicatorModule
} from '../../../components/partial-loading-indicator';
import {L10nModule} from '../../../components/translate';
import {ValueChangeAnimationPipe} from '../../../components/value-change-animation/value-change-animation.pipe';
import {AppConfigService} from '../../../services/config.service';
import {DexWalletWidgetFilterSettings} from '../settings/dex-wallet-widget-settings';
import {DynDisplayAssetFactory} from './dyn-display-asset.factory';
import {DynDisplayAssetComponent} from './dyn-display-asset/dyn-display-asset.component';
import {DynAsset, DynDisplayAsset, DynWalletRowPayload} from './dyn-wallet-row';

interface BalanceRec {
    wallet: DynWallet;
    tokenBalances: DeliverableBalance[];
}

@Component({
    selector:        'dyn-single-wallet-balances',
    templateUrl:     './dyn-single-wallet-balances.component.html',
    styleUrl:        './dyn-single-wallet-balances.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        PartialLoadingIndicatorModule,
        L10nModule,
        DynDisplayAssetComponent,
        DisplayDecimalModule,
        BigintDisplayDecimalPipe,
        CustomScrollbarModule,
        NgClass,
        ValueChangeAnimationPipe
    ],
    providers:       [
        DynDisplayAssetFactory
    ]
})
export class DynSingleWalletBalancesComponent implements OnInit, OnChanges {

    @Input()
    public filters?: DexWalletWidgetFilterSettings;

    @Input('refresh$')
    public externalRefresh$?: Observable<void>;

    @ViewChild('loadingEl', {read: PARTIAL_LOADING, static: true})
    public loadingIndicator: PartialLoading;

    public readonly destroy$ = destroy$();

    public readonly $isEmptyBalances = signal<boolean>(false);

    public readonly $rows = signal<DynWalletRowPayload[]>([]);

    public readonly $isFreshFetching = signal<boolean>(true);

    private readonly refreshBalancesSubscription$ = new Subject<void>();

    private readonly displayAssetCacheByKey = new Map<string, DynDisplayAsset>();

    private readonly dynDisplayAssetFactory = inject(DynDisplayAssetFactory);
    private readonly erc20BalancesService = inject(Erc20BalancesService);
    private readonly erc20BalanceUpdatesService = inject(Erc20BalanceUpdatesService);
    private readonly dynamicZyxDaemonService = inject(DynamicZyxDaemonService);
    private readonly appConfigService = inject(AppConfigService);

    public ngOnInit(): void {
        this.listenToBalances();
    }

    public ngOnChanges(changes: TypedSimpleChanges<DynSingleWalletBalancesComponent>) {
        if (changes.filters && !changes.filters.firstChange) {
            console.warn('filters changed', this.filters); // TODO

            this.$rows.set([]);
            this.refreshBalancesSubscription$.next();
        }
    }

    private listenToBalances(): void {
        const hideLoadingIndicator$ = new Subject<void>();

        merge(this.refreshBalancesSubscription$, this.externalRefresh$ || NEVER).pipe(
            startWith(undefined),
            switchMap(() => this.createBalancesStream()),
            debounceTime(UPDATES_GATING_PERIOD),
            tap(balanceRec => {
                hideLoadingIndicator$.next();
                this.processAccountBalanceUpdates(balanceRec);

                console.warn({balanceRec}); // TODO

                this.logBalances(balanceRec);
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private processAccountBalanceUpdates(balanceRec: BalanceRec): void {
        let rows: DynWalletRowPayload[];

        if (balanceRec) {
            const filterFn = this.createFilterFn();
            const filteredBalances: DeliverableBalance[] = filterFn
                ? balanceRec.tokenBalances.filter(filterFn)
                : balanceRec.tokenBalances;

            console.warn({filteredBalances}); // TODO

            rows = filteredBalances
                .map(balance => {
                    const displayAsset = this.findOrCreateDisplayAsset(balance);

                    return {
                        key:          displayAsset.symbol,
                        account:      balanceRec.wallet,
                        displayAsset: displayAsset,
                        isAsset:      true,
                        values:       {
                            qty: balance.balance
                        }
                    } as DynWalletRowPayload;
                })
                .sort((a, b) =>
                    (b.values.qty ? 1 : 0) - (a.values.qty ? 1 : 0)
                    || a.displayAsset.symbol.localeCompare(b.displayAsset.symbol)
                );
        } else {
            rows = [];
        }

        this.$rows.set(rows);
        this.$isEmptyBalances.set(!rows.length);

        console.warn(rows); // TODO
    }

    private createFilterFn(): (balance: DeliverableBalance) => boolean {
        const createSymbolFilterFn = () => {
            const preparedSymbolFilter = this.filters.assetSymbol.toLowerCase();

            return (balance: DeliverableBalance): boolean =>
                balance.deliverable.symbol.toLowerCase().startsWith(preparedSymbolFilter);
        };

        const createSupportedOnlyFilterFn = () => {
            const knownSymbolToAddrMap = new Map<string, string>();

            console.warn('createSupportedOnlyFilterFn'); // TODO
            (window as any).knownSymbolToAddrMap = knownSymbolToAddrMap; // TODO

            // TODO: make no sense, while we are fetching balances for deliverables only
            return (balance: DeliverableBalance): boolean => {
                let isOk: boolean = true;
                const knownAddr = knownSymbolToAddrMap.get(balance.deliverable.symbol);

                if (knownAddr) {
                    isOk = balance.deliverable.identifier === knownAddr;
                } else {
                    knownSymbolToAddrMap.set(balance.deliverable.symbol, balance.deliverable.identifier);
                }

                return isOk;
            };
        };

        const isSymbolFilterSet = !!this.filters?.assetSymbol;
        const isOnlySupportedOnlyFilterSet = !!this.filters?.isOnlySupportedAssets;

        const symbolFilterFn = isSymbolFilterSet
            ? createSymbolFilterFn()
            : () => true;

        const onlySupportedOnlyFilterFn = isOnlySupportedOnlyFilterSet
            ? createSupportedOnlyFilterFn()
            : () => true;

        return (isSymbolFilterSet || isOnlySupportedOnlyFilterSet)
            ? balance => symbolFilterFn(balance) && onlySupportedOnlyFilterFn(balance)
            : undefined;
    }

    private createBalancesStream(): Observable<BalanceRec> {
        return this.dynamicZyxDaemonService.dynRemote.primaryWallet$.pipe(
            distinctUntilChanged((w1, w2) => w1?.address === w2?.address),
            switchMap(wallet => {
                if (!wallet) {
                    return of(null);
                }

                const tokenBalances$ = this.erc20BalancesService.fetchWalletTokenBalances(
                    wallet.address
                );

                const partialUpdates$ = this.erc20BalanceUpdatesService.getUpdatesStream({
                    wAddresses: [wallet.address]
                });

                return tokenBalances$.pipe(
                    dependOn(this.loadingIndicator.loading$),
                    this.mergeBalancesWithUpdates(wallet.address, partialUpdates$),
                    map(tokenBalances => ({
                        wallet,
                        tokenBalances
                    } satisfies BalanceRec))
                );
            })
        );
    }

    private mergeBalancesWithUpdates(
        wAddress: string,
        partial$: Observable<PartialBalanceUpdate>
    ): MonoTypeOperatorFunction<DeliverableBalance[]> {
        return source => new Observable<DeliverableBalance[]>(observer => {
            let merged: DeliverableBalance[] = [];

            const sourceSubscription = source.subscribe(full => {
                merged = full;
                this.$isFreshFetching.set(true);
                observer.next(merged);
            });
            const partialSubscription = partial$.subscribe(partial => {
                if (partial && wAddress === partial.wAddress) {
                    merged = merged.length
                        ? merged.map(b => {
                            const foundInPartial = partial.tokenBalances.find(it =>
                                it.deliverable.deliverableId === b.deliverable.deliverableId
                            );

                            return foundInPartial || b;
                        })
                        : partial.tokenBalances;
                }

                this.$isFreshFetching.set(false);
                observer.next(merged);
            });

            return () => {
                partialSubscription.unsubscribe();
                sourceSubscription.unsubscribe();
            };
        });
    }

    private findOrCreateDisplayAsset(tokenBalance: DeliverableBalance): DynDisplayAsset {
        const asset: DynAsset = {
            networkId:   this.appConfigService.appConfig.dex.chain.id,
            address:     tokenBalance.deliverable.identifier,
            name:        tokenBalance.deliverable.symbol,
            symbol:      tokenBalance.deliverable.symbol,
            decimals:    tokenBalance.deliverable.decimalPlaces,
            logoURI:     '', // TODO
            deliverable: tokenBalance.deliverable
        };

        const assetCacheKey = this.dynDisplayAssetFactory.getAssetCacheKey(asset);
        let displayAsset: DynDisplayAsset = this.displayAssetCacheByKey.get(assetCacheKey);

        if (!displayAsset) {
            displayAsset = this.dynDisplayAssetFactory.createDisplayAsset(asset);
            this.displayAssetCacheByKey.set(assetCacheKey, displayAsset);
        }

        return displayAsset;
    }

    private logBalances(balanceRec: BalanceRec): void {
        const dict = balanceRec.tokenBalances.reduce(
            (acc, rec) => {
                acc[rec.deliverable.symbol] = vToDec(rec.balance, rec.deliverable.decimalPlaces).toString();

                return acc;
            },
            {} as Record<string, string>
        );

        console.log('[Balances] Dict', dict);
    }

}
